import type { ConflictRow } from '@/types/nf'
import { findRuneSelection, type RuneSelection } from '@/utils/partialRegenerate'

export interface ConflictContextWindow {
  text: string
  highlightStart: number
  highlightEnd: number
}

export function locateEntity(content: string, row: ConflictRow): RuneSelection | null {
  const candidates = [...new Set([row.entity_name, row.entity_id].map((value) => value.trim()).filter(Boolean))]
  for (const candidate of candidates) {
    const selection = findRuneSelection(content, candidate)
    if (selection) return selection
  }
  return null
}

export interface ApproximateLocation {
  selection: RuneSelection
  /** 实际命中的文本；与实体名不同，说明是近似匹配。 */
  matchedText: string
}

/**
 * 精确匹配失败时的近似定位。
 *
 * 「找不到精确匹配」恰恰是【最需要上下文】的场景：用户此刻要判断的正是
 * “是抽取器错了，还是我写错了”。而原实现在这里直接放弃、回退去渲染正文首段——
 * 对 markdown 章节来说首段就是标题行，于是弹窗里只剩一个章节名。
 *
 * 真实例子（E2E 项目「沈九」）：实体名「三十年前旧事」在正文中不存在，
 * 正文写的是「三十年前的旧事」。取最长可命中子串就能定位到「三十年前」，
 * 用户一眼就能看出是抽取器把叙述短语当成了专名。
 *
 * 只接受长度 >= 2 且不短于实体名一半的子串：太短的片段（单字）会命中
 * 无关位置，把“给不出上下文”换成“给了错的上下文”，那更糟。
 */
export function locateEntityApproximately(content: string, row: ConflictRow): ApproximateLocation | null {
  const names = [...new Set([row.entity_name, row.entity_id].map((value) => value.trim()).filter(Boolean))]
  for (const name of names) {
    const runes = Array.from(name)
    const minLen = Math.max(2, Math.ceil(runes.length / 2))
    for (let len = runes.length - 1; len >= minLen; len -= 1) {
      for (let start = 0; start + len <= runes.length; start += 1) {
        const fragment = runes.slice(start, start + len).join('')
        const selection = findRuneSelection(content, fragment)
        if (selection) return { selection, matchedText: fragment }
      }
    }
  }
  return null
}

export function buildContextWindow(
  content: string,
  anchor: RuneSelection | null,
  runeBudget = 400,
): ConflictContextWindow | null {
  if (!content || !anchor) return null

  const runes = Array.from(content)
  const start = Math.max(0, Math.min(anchor.start, runes.length))
  const end = Math.max(start, Math.min(anchor.end, runes.length))
  if (start === end) return null

  const budget = Math.max(0, runeBudget)
  const windowStart = Math.max(0, start - budget)
  const windowEnd = Math.min(runes.length, end + budget)

  return {
    text: runes.slice(windowStart, windowEnd).join(''),
    highlightStart: start - windowStart,
    highlightEnd: end - windowStart,
  }
}
